import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Loader2, CreditCard, Check, X, Trash2, Calendar, Mail, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending:  "bg-amber-500/20 text-amber-300 border-amber-500/40",
  approved: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  rejected: "bg-red-500/20 text-red-300 border-red-500/40",
};

export default function AdminSubscriptions() {
  const { getAdminAxios } = useOutletContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [filter, setFilter] = useState("pending");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getAdminAxios().get(`${API}/admin/subscriptions`);
      setItems(data);
    } finally { setLoading(false); }
  }, [getAdminAxios]);

  useEffect(() => { refresh(); }, [refresh]);

  const approve = async (sub) => {
    const days = window.prompt(`¿Por cuántos días activar el plan ${sub.plan_name}?`, sub.billing_period === "yearly" ? "365" : "30");
    if (!days || isNaN(parseInt(days))) return;
    setBusy({ ...busy, [sub.id]: true });
    try {
      await getAdminAxios().post(`${API}/admin/subscriptions/${sub.id}/approve`, { days: parseInt(days) });
      await refresh();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setBusy({ ...busy, [sub.id]: false }); }
  };

  const reject = async (sub) => {
    if (!window.confirm(`¿Rechazar la solicitud de ${sub.name}?`)) return;
    setBusy({ ...busy, [sub.id]: true });
    try {
      await getAdminAxios().post(`${API}/admin/subscriptions/${sub.id}/reject`);
      await refresh();
    } finally { setBusy({ ...busy, [sub.id]: false }); }
  };

  const remove = async (sub) => {
    if (!window.confirm(`¿Eliminar la solicitud (${sub.email})?`)) return;
    await getAdminAxios().delete(`${API}/admin/subscriptions/${sub.id}`);
    await refresh();
  };

  const filtered = filter === "all" ? items : items.filter((x) => x.status === filter);
  const counts = items.reduce((acc, x) => ({ ...acc, [x.status]: (acc[x.status] || 0) + 1 }), {});

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-subscriptions-page">
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-2"><CreditCard className="w-6 h-6" /> Pagos / Suscripciones</h1>
          <p className="text-sm text-slate-400 mt-1">Aprobá los pagos por transferencia para activar el plan al cliente.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {["pending", "approved", "rejected", "all"].map((f) => (
            <Button key={f} variant={filter === f ? "default" : "outline"}
                    onClick={() => setFilter(f)}
                    className={filter === f ? "bg-amber-500 text-black hover:bg-amber-600" : ""}
                    data-testid={`filter-${f}`}>
              {f === "pending" ? "Pendientes" : f === "approved" ? "Aprobadas" : f === "rejected" ? "Rechazadas" : "Todas"}
              {counts[f] != null && f !== "all" && <span className="ml-2 px-2 py-0.5 rounded-full bg-black/30 text-xs">{counts[f]}</span>}
            </Button>
          ))}
        </div>
      </div>

      {loading ? <Loader2 className="w-6 h-6 animate-spin text-amber-400" /> : (
        <div className="space-y-3">
          {filtered.length === 0 && <p className="text-sm text-slate-500">Sin solicitudes en este estado.</p>}
          {filtered.map((s) => (
            <Card key={s.id} className="bg-[#13112b] border-indigo-900/30" data-testid={`sub-${s.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-white">{s.name}</h3>
                      <Badge className={STATUS_BADGE[s.status] + " border"}>{s.status}</Badge>
                      <Badge variant="outline">{s.plan_name} · {s.billing_period}</Badge>
                    </div>
                    <div className="flex gap-4 flex-wrap text-xs text-slate-400">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{s.email}</span>
                      {s.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{s.phone}</span>}
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{(s.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <p className="text-sm text-amber-300">${s.price} {s.currency} · {s.payment_method}</p>
                    {s.notes && <p className="text-xs text-slate-500">📝 {s.notes}</p>}
                    {s.expires_on && s.status === "approved" && (
                      <p className="text-xs text-emerald-300">✓ Vence el {s.expires_on.slice(0, 10)}</p>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {s.status === "pending" && (
                      <>
                        <Button onClick={() => approve(s)} disabled={!!busy[s.id]} className="bg-emerald-500 hover:bg-emerald-600 text-black" data-testid={`approve-${s.id}`}>
                          {busy[s.id] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                          Aprobar
                        </Button>
                        <Button onClick={() => reject(s)} disabled={!!busy[s.id]} variant="outline" className="border-red-500/30 text-red-400" data-testid={`reject-${s.id}`}>
                          <X className="w-3 h-3 mr-1" /> Rechazar
                        </Button>
                      </>
                    )}
                    <Button onClick={() => remove(s)} variant="outline" size="icon" className="border-red-500/30 text-red-400 h-9 w-9">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
